import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import ServiceSection from "@/components/ServiceSection";
import SEO from "@/components/SEO";
import FAQSection from "@/components/FAQSection";
import { 
  Users, 
  Video, 
  Palette, 
  Camera, 
  Globe, 
  MessageSquare,
  Lightbulb,
  UserPlus,
  TrendingUp,
  Heart,
  Play
} from "lucide-react";

const RecruitmentPage = () => {
  const services = [
    {
      icon: Lightbulb,
      title: "Strategy & Employer Brand Development",
      description: "We help you define what makes you a great employer and craft messaging that resonates with your ideal candidates. EVP development that sets you apart from competitors.",
      services: [
        "Employer value proposition (EVP) development",
        "Candidate persona mapping",
        "Competitor employer analysis",
        "Campaign strategy and planning",
        "Messaging framework creation"
      ],
      image: "https://customer-assets.emergentagent.com/job_2f219309-49d2-4096-9b1f-9aa2825973b1/artifacts/g1be1bfw_A001_03262248_C154.braw.22_49_39_10.Still001.jpg",
      altText: "Employer brand strategy development and EVP planning session"
    },
    {
      icon: Video,
      title: "Video Production",
      description: "Authentic employer brand videos that showcase your culture, not corporate speak. Real employees, real stories, real reasons to apply.",
      services: [
        "Employer brand films",
        "Day-in-the-life content",
        "Employee testimonials",
        "Role and team showcases",
        "Office and facility tours"
      ],
      image: "https://customer-assets.emergentagent.com/job_2f219309-49d2-4096-9b1f-9aa2825973b1/artifacts/yn3cs337_shaun%20%282%29.jpg",
      altText: "Recruitment marketing video production for employer branding campaign"
    },
    {
      icon: Palette,
      title: "Graphic Design",
      description: "Recruitment marketing materials that stand out on job boards, at careers fairs, and across social media. Consistent branding that builds employer recognition.",
      services: [
        "Job advert design",
        "Careers fair materials",
        "Social media graphics",
        "Recruitment brochures",
        "Candidate welcome packs"
      ],
      image: "https://customer-assets.emergentagent.com/job_people-comms-hub/artifacts/mxx4boct_keith%20cropped%202.jpg",
      altText: "Graphic design for recruitment marketing materials and employer brand collateral"
    },
    {
      icon: Camera,
      title: "Professional Photography",
      description: "Candidates can spot stock photography a mile off. We shoot real employees in real workplaces, giving future hires an honest look at your culture and the people they'll actually be working alongside.",
      services: [
        "Workplace environment shoots",
        "Team and individual portraits",
        "Event and social photography",
        "Behind-the-scenes captures",
        "Multi-location coverage"
      ],
      image: "https://customer-assets.emergentagent.com/job_2f219309-49d2-4096-9b1f-9aa2825973b1/artifacts/7n0qmrhh_keith%20camera.jpg",
      altText: "Authentic employee photography for recruitment campaigns replacing stock images"
    },
    {
      icon: MessageSquare,
      title: "Social Media & Content",
      description: "Ongoing employer brand content that keeps you visible to passive candidates. Employee stories, culture highlights, and engaging campaigns.",
      services: [
        "Social media strategy",
        "Content calendar management",
        "Employee advocacy programmes",
        "Candidate journey content",
        "Campaign copywriting"
      ],
      image: "https://customer-assets.emergentagent.com/job_2f219309-49d2-4096-9b1f-9aa2825973b1/artifacts/9i39wixn_social%20camera.jpg",
      altText: "Social media content creation for recruitment marketing and talent attraction"
    },
    {
      icon: Globe,
      title: "Careers Website Development",
      description: "Dedicated careers sites that convert visitors into applicants. Showcase your culture, display opportunities, and make applying seamless.",
      services: [
        "Careers microsite development",
        "ATS integration",
        "Role landing pages",
        "Culture showcase sections",
        "Mobile-optimised applications"
      ],
      image: "https://customer-assets.emergentagent.com/job_2f219309-49d2-4096-9b1f-9aa2825973b1/artifacts/7a3tual8_sheffield.jpg",
      altText: "Recruitment careers website designed for employer branding and talent attraction"
    }
  ];

  const benefits = [
    {
      icon: UserPlus,
      title: "Attract Better Talent",
      description: "Compelling employer branding attracts candidates who align with your culture and values."
    },
    {
      icon: TrendingUp,
      title: "Reduce Cost Per Hire",
      description: "Strong employer brand means less reliance on expensive agencies and job boards."
    },
    {
      icon: Heart,
      title: "Improve Retention",
      description: "Candidates who apply for the right reasons stay longer and perform better."
    }
  ];

  return (
    <div data-testid="recruitment-page" className="min-h-screen bg-[#050505]">
      <SEO
        title="Recruitment Marketing & Employer Branding for Large Employers"
        description="Attract and retain top talent with authentic recruitment marketing. Employer brand videos, careers websites, EVP development and recruitment campaigns for organisations with large workforces looking to reduce cost per hire and improve staff retention."
        keywords="recruitment marketing, employer branding, recruitment videos, employer brand, talent attraction, recruitment campaigns, EVP development, careers website, recruitment content, employer brand agency, staff retention, reduce cost per hire"
        canonical="/recruitment"
      />
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center" data-testid="recruitment-hero">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1920"
            alt="Recruitment marketing employer branding campaign photography"
            className="w-full h-full object-cover"
            decoding="async"
            fetchpriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/90 to-transparent" />
          <div className="absolute inset-0 hero-overlay" />
        </div>

        <div className="container mx-auto px-4 md:px-8 relative z-10 pt-32">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-lg bg-[#00E5FF]/20 flex items-center justify-center">
                <Users className="w-6 h-6 text-[#00E5FF]" />
              </div>
              <span className="text-[#00E5FF] font-semibold uppercase tracking-wider">
                Internal Communications
              </span>
            </div>

            <h1 className="font-display text-5xl md:text-7xl tracking-tight leading-none text-white mb-6">
              RECRUITMENT
            </h1>

            <p className="text-lg md:text-xl text-[#B3B3B3] max-w-2xl mb-8">
              Attract the talent you deserve. We create authentic employer brand 
              campaigns that showcase your real people and culture — helping large organisations 
              reduce cost per hire, improve staff retention, and make top candidates want to work for you.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button
                  data-testid="recruitment-book-call-btn"
                  className="bg-[#00E5FF] text-black hover:bg-[#00B8CC] font-bold px-8 py-6 uppercase tracking-wider text-base"
                >
                  <Play className="w-5 h-5 mr-2 fill-black" />
                  Book a Call
                </Button>
              </Link>
              <Link to="/case-studies">
                <Button
                  data-testid="recruitment-case-studies-btn"
                  variant="outline"
                  className="bg-white/10 text-white hover:bg-white/20 border-white/10 px-8 py-6 uppercase tracking-wider text-base"
                >
                  View Case Studies
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="py-10 md:py-16 bg-[#0a0a0a]" data-testid="recruitment-problem">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-display text-3xl md:text-5xl tracking-wide text-white mb-8">
              IN THE WAR FOR TALENT,
              <br />
              <span className="text-[#00E5FF]">INVISIBLE EMPLOYERS LOSE</span>
            </h2>
            <p className="text-[#B3B3B3] text-lg leading-relaxed mb-8">
              Your competitors are showcasing their culture, celebrating their 
              people, and building employer brands that attract top talent. 
              Meanwhile, your job adverts get lost in the noise, your careers 
              page hasn't been updated in years, and great candidates don't even 
              know you exist.
            </p>
            <p className="text-white text-xl font-medium">
              We change that. Through strategic employer branding, compelling 
              content, and consistent presence, we make your company the one 
              everyone wants to work for.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-10 md:py-16 bg-[#050505]" data-testid="recruitment-benefits">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="p-8 bg-[#141414] rounded-lg border border-[#1F1F1F] hover:border-[#00E5FF]/30 transition-colors"
                data-testid={`benefit-${index}`}
              >
                <div className="w-14 h-14 rounded-lg bg-[#00E5FF]/10 flex items-center justify-center mb-6">
                  <benefit.icon className="w-7 h-7 text-[#00E5FF]" />
                </div>
                <h3 className="font-display text-xl tracking-wide text-white mb-3">
                  {benefit.title}
                </h3>
                <p className="text-[#B3B3B3]">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-10 md:py-16 bg-[#0a0a0a]" data-testid="recruitment-services">
        <div className="container mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <h2 className="font-display text-4xl md:text-5xl tracking-wide text-white mb-4">
              WHAT WE DELIVER
            </h2>
            <p className="text-[#B3B3B3] text-lg max-w-2xl mx-auto">
              End-to-end employer branding services that attract, engage, and 
              convert your ideal candidates.
            </p>
          </div>

          <div className="space-y-20">
            {services.map((service, index) => (
              <ServiceSection
                key={index}
                {...service}
                reverse={index % 2 === 1}
                accentColor="#00E5FF"
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <FAQSection faqs={[
        { question: "What is recruitment marketing?", answer: "Recruitment marketing applies marketing principles to talent attraction. Instead of simply posting job ads, we create compelling employer brand content — videos, photography, social campaigns and careers websites — that showcase your culture, people and opportunities to attract candidates who are the right fit." },
        { question: "How does employer branding reduce cost per hire?", answer: "A strong employer brand means candidates come to you. When people already know and admire your organisation as an employer, you spend less on job boards and recruitment agencies. Authentic content showing real employees and genuine culture builds trust and attracts higher-quality applicants." },
        { question: "Why shouldn't we use stock photography in recruitment?", answer: "Candidates can spot stock imagery instantly — and it immediately undermines trust. If your recruitment materials show generic models instead of real employees, candidates question what you're hiding. Authentic photography of your actual people and workplaces is far more compelling and credible." },
        { question: "How does recruitment marketing improve staff retention?", answer: "When candidates join based on an honest, authentic portrayal of your organisation, they arrive with realistic expectations. There's no disconnect between what was promised and what they experience. This alignment dramatically reduces early attrition and builds longer-term commitment." }
      ]} />

      <section className="py-20 md:py-32 bg-[#050505] relative overflow-hidden" data-testid="recruitment-cta">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#00E5FF] rounded-full blur-[150px]" />
        </div>
        
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-display text-4xl md:text-5xl tracking-wide text-white mb-6">
              READY TO WIN THE
              <br />
              <span className="text-[#00E5FF]">WAR FOR TALENT?</span>
            </h2>
            <p className="text-[#B3B3B3] text-lg mb-8">
              Let's discuss how we can help you attract, engage, and hire the 
              talent your business deserves.
            </p>
            <Link to="/contact">
              <Button
                data-testid="recruitment-cta-btn"
                className="bg-[#00E5FF] text-black hover:bg-[#00B8CC] font-bold px-10 py-6 uppercase tracking-wider text-base"
              >
                Start Attracting Top Talent
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RecruitmentPage;
